import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback
} from "react";
import { X, Zap, LucideIcon, CircleCheck } from "lucide-react";
import VoiceCallInterface from "../voiceCallInterface";
import retellWebClient from "@/utils/retell-web-client";
import aposTropheIcon from "@/assets/invertedcommas.svg";
import { AgentConfigService } from "@/test-2/api/agent-config-api";
import registerCallRetell from "../../utils/register-call";

interface TranscriptMessage {
  speaker: string;
  text: string;
  time: string;
  type: "user" | "agent";
}

// Retell transcript interfaces
interface RetellTranscript {
  content: string;
  role: "agent" | "user";
}

interface RetellUpdate {
  event_type: string;
  transcript?: RetellTranscript[];
}

interface RetellCallDialogProps {
  pageCategory: string;
  onEndCall: () => void;
  agentName: string;
  callHints: any;
  icon: any;
  agentId: string;
  phoneNumber: string;
  callTitle: string;
  isDialogOpen: boolean;
  try_speaking: string[] | [];
  activities: string[] | [];
  toggleDialog: () => void;
  features: string[];
  voiceId?: string;
  defaultVoiceId?: string;
}

interface SidebarSectionProps {
  title: string;
  children: React.ReactNode;
  className?: string;
}

interface SuggestionItemProps {
  text: string;
  icon?: string;
}

interface HintItemProps {
  text: string;
}

interface FeatureItemProps {
  text: string;
  enabled?: boolean;
}

interface ActivityItemProps {
  text: string;
  icon: LucideIcon;
  status?: "active" | "completed" | "warning";
}

interface TranscriptMessageProps {
  text: string;
  type?: "user" | "agent";
}

const SidebarSection: React.FC<SidebarSectionProps> = ({
  title,
  children,
  className = ""
}) => (
  <div className={`${className}`}>
    <h2 className="text-xl font-normal text-gray-400 mb-8 text-left font-inter">
      {title}
    </h2>
    {children}
  </div>
);

const SuggestionItem: React.FC<SuggestionItemProps> = ({
  text,
  icon = '"'
}) => (
  <div className="flex items-start space-x-2">
    <img src={aposTropheIcon} className="w-4 h-4" />
    <p className="text-gray-300 text-base md:text-xl font-manrope">{text}</p>
  </div>
);

const HintItem: React.FC<HintItemProps> = ({ text }) => (
  <div className="flex items-center space-x-3">
    <div className="w-2 h-2 bg-white rounded-full"></div>
    <p className="text-gray-300  text-base md:text-xl font-manrope">{text}</p>
  </div>
);

const FeatureItem: React.FC<FeatureItemProps> = ({ text, enabled = true }) => (
  <div className="flex items-center space-x-3">
    <CircleCheck
      size={16}
      className={enabled ? "text-green-400" : "text-gray-500"}
    />
    <p
      className={` text-base md:text-xl font-manrope ${enabled ? "text-gray-300" : "text-gray-500"
        }`}
    >
      {text}
    </p>
  </div>
);

const ActivityItem: React.FC<ActivityItemProps> = ({
  text,
  icon: Icon,
  status = "active"
}) => (
  <div className="flex items-center space-x-4">
    <Icon size={16} className="text-gray-400" />
    <p className="text-gray-300  text-base md:text-xl font-manrope">{text}</p>
  </div>
);

const TranscriptMessage: React.FC<TranscriptMessageProps> = ({
  text,
  type = "user"
}) => (
  <div
    className={`mb-2 ${type === "user" ? "ml-auto" : "mr-auto"} max-w-[80%]`}
  >
    <div
      className={`px-4 py-1 ${type === "user" ? "text-white" : "text-gray-100"}`}
    >
      <div className="flex items-center justify-between mb-1">
        <p
          className={`text-base sm:text-xl leading-relaxed font-manrope text-left ${type === "user"
            ? "text-gray-500"
            : "text-white"
            }`}
        >
          {text}
        </p>
      </div>
    </div>
  </div>
);


const useTranscriptManager = (agentName: string) => {
  const [transcripts, setTranscripts] = useState<TranscriptMessage[]>([]);

  const [currentMessage, setCurrentMessage] = useState<{
    role: "agent" | "user" | null;
    content: string;
    startTime: Date;
  }>({ role: null, content: "", startTime: new Date() });

  const messageTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const MESSAGE_COMPLETION_DELAY = 2000;

  const currentMessageRef = useRef(currentMessage);
  const transcriptsRef = useRef(transcripts);

  useEffect(() => {
    currentMessageRef.current = currentMessage;
  }, [currentMessage]);

  useEffect(() => {
    transcriptsRef.current = transcripts;
  }, [transcripts]);

  const formatTime = (date: Date): string =>
    date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  const getSpeakerName = useCallback(
    (role: "agent" | "user"): string => (role === "agent" ? agentName : "User"),
    [agentName]
  );

  const finalizeCurrentMessage = useCallback(() => {
    const current = currentMessageRef.current;

    if (current.role && current.content.trim()) {
      const newTranscript: TranscriptMessage = {
        speaker: getSpeakerName(current.role),
        text: current.content.trim(),
        time: formatTime(current.startTime),
        type: current.role
      };

      setTranscripts((prev) => {
        const lastTranscript = prev[prev.length - 1];

        if (
          lastTranscript &&
          lastTranscript.text === newTranscript.text &&
          lastTranscript.type === newTranscript.type
        ) {
          console.log("Prevented duplicate transcript:", newTranscript.text);
          return prev;
        }

        return [...prev, newTranscript];
      });

      setCurrentMessage({ role: null, content: "", startTime: new Date() });
    }
  }, [getSpeakerName]);

  const processTranscriptUpdate = useCallback(
    (retellTranscripts: RetellTranscript[]) => {
      if (!retellTranscripts || retellTranscripts.length === 0) return;

      const latestTranscript = retellTranscripts[retellTranscripts.length - 1];

      const current = currentMessageRef.current;

      if (
        current.role === latestTranscript.role &&
        current.content === latestTranscript.content
      ) {
        console.log("Skipping identical transcript update");
        return;
      }

      if (messageTimeoutRef.current) clearTimeout(messageTimeoutRef.current);

      if (current.role !== latestTranscript.role) {
        finalizeCurrentMessage();
        setCurrentMessage({
          role: latestTranscript.role,
          content: latestTranscript.content,
          startTime: new Date()
        });
      } else {
        setCurrentMessage((prev) => ({
          ...prev,
          content: latestTranscript.content
        }));
      }

      messageTimeoutRef.current = setTimeout(() => {
        finalizeCurrentMessage();
      }, MESSAGE_COMPLETION_DELAY);
    },
    [finalizeCurrentMessage]
  );

  useEffect(() => {
    return () => {
      if (messageTimeoutRef.current) clearTimeout(messageTimeoutRef.current);
    };
  }, []);

  return useMemo(
    () => ({
      transcripts,
      currentMessage,
      processTranscriptUpdate,
      finalizeCurrentMessage
    }),
    [transcripts, currentMessage]
  );
};

const RetellCallDashboard: React.FC<RetellCallDialogProps> = ({
  onEndCall,
  pageCategory,
  agentName,
  callHints,
  callTitle,
  icon,
  agentId,
  phoneNumber,
  isDialogOpen,
  toggleDialog,
  try_speaking,
  features,
  activities,
  voiceId,
  defaultVoiceId
}) => {
  const {
    transcripts,
    currentMessage,
    processTranscriptUpdate,
    finalizeCurrentMessage
  } = useTranscriptManager(agentName);

  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(
    null
  );
  const [stream, setStream] = useState<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const [isCalling, setIsCalling] = useState<boolean>(false);
  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const timerRef = useRef<number | null>(null);
  const transcriptDesktopRef = useRef<HTMLDivElement>(null);
  const transcriptMobileRef = useRef<HTMLDivElement>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [callEnded, setCallEnded] = useState<boolean>(false);
  const [seconds, setSeconds] = useState(0);

  const [activeTab, setActiveTab] = useState("transcript");

  const tabs = [
    { id: "transcript", label: "Transcript" },
    { id: "try-speaking", label: "Try Speaking" },
    { id: "call-hints", label: "Call Hints" },
    { id: "activities", label: "Activities" },
    { id: "features", label: "Features" }
  ];


  const renderMobileTabContent = () => {
    switch (activeTab) {
      case "transcript":
        return (
          <div className="h-[30vh] text-sm rounded-lg overflow-y-auto space-y-1 scroll-smooth">
            {transcripts.length === 0 && !currentMessage.content ? (
              <div className="flex flex-col items-center justify-center h-full">
                <div className="w-12 h-12 mb-3 rounded-full bg-gray-800/50 flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                </div>
                <h4 className="text-base font-medium text-gray-500 mb-1 text-center">
                  Your conversation with {agentName} will appear here
                </h4>
              </div>
            ) : (
              <>
                {transcripts.map((message, index) => (
                  <TranscriptMessage
                    key={index}
                    text={message.text}
                    type={message.type}
                  />
                ))}
                {currentMessage.role && currentMessage.content && (
                  <div className="opacity-75">
                    <TranscriptMessage text={currentMessage.content + "..."} />
                  </div>
                )}
              </>
            )}
            <div ref={transcriptMobileRef} />
          </div>
        );

      case "try-speaking":
        return (
          <div className="space-y-3 text-sm">
            {try_speaking.map((suggestion, index) => (
              <SuggestionItem key={index} text={suggestion} />
            ))}
          </div>
        );

      case "call-hints":
        return (
          <div className="space-y-3">
            {callHints.map((hint: any, index: number) => (
              <HintItem key={index} text={hint} />
            ))}
          </div>
        );

      case "activities":
        return (
          <div className="space-y-6">
            {activities.map((activity, index) => (
              <ActivityItem
                key={index}
                text={activity}
                icon={Zap}
                status="active"
              />
            ))}
          </div>
        );

      case "features":
        return (
          <div className="space-y-3">
            {features.map((feature, index) => (
              <FeatureItem key={index} text={feature} />
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  const startTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    setSeconds(0);
    timerRef.current = window.setInterval(() => {
      setSeconds((prev) => prev + 1);
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const askForMicrophonePermissions = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (error) {
      console.error("Microphone permission error:", error);
      handleEndCall();
    }
  };

  const initializeCall = async () => {
    try {
      setIsConnecting(true);

      const registerCallResponse = await registerCallRetell(agentId, voiceId);

      if (registerCallResponse.call_id) {
        await retellWebClient.startCall({
          accessToken: registerCallResponse.access_token,
          sampleRate: registerCallResponse.sample_rate
        });
        startTimer();
        setIsCalling(true);
      }
    } catch (error) {
      console.error("Error starting conversation:", error);
      handleEndCall();
    }
  };

  const handleEndCall = async () => {
    if (!isCalling) return;
    stopTimer();
    retellWebClient.stopCall();
    setIsCalling(false);
    setCallEnded(true);
    setIsConnecting(false);
    finalizeCurrentMessage();
  };

  const handleChat = () => {
    console.log("Chat opened");
    // Handle chat logic
  };

  useEffect(() => {
    if (!isCalling) {
      finalizeCurrentMessage();
    }
  }, [isCalling, finalizeCurrentMessage]);

  useEffect(() => {
    if (transcriptDesktopRef.current) {
      transcriptDesktopRef.current.scrollIntoView({
        behavior: "smooth"
      });
    }

    if (transcriptMobileRef.current) {
      transcriptMobileRef.current.scrollIntoView({
        behavior: "smooth"
      });
    }
  }, [transcripts, currentMessage]);

  useEffect(() => {
    const handleUpdate = (update: RetellUpdate) => {
      if (isConnecting) setIsConnecting(false);
      if (update.transcript && Array.isArray(update.transcript)) {
        processTranscriptUpdate(update.transcript);
      }
    };

    retellWebClient.on("conversationEnded", handleEndCall);
    retellWebClient.on("error", handleEndCall);
    retellWebClient.on("update", handleUpdate);

    return () => {
      retellWebClient.off("conversationEnded", handleEndCall);
      retellWebClient.off("error", handleEndCall);
      retellWebClient.off("update", handleUpdate);
    };
  }, []);

  useEffect(() => {
    askForMicrophonePermissions();

    const startRecording = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true
        });
        setStream(stream);

        audioContextRef.current = new AudioContext();
        const source = audioContextRef.current.createMediaStreamSource(stream);

        const highPassFilter = audioContextRef.current.createBiquadFilter();
        highPassFilter.type = "highpass";
        highPassFilter.frequency.setValueAtTime(
          400,
          audioContextRef.current.currentTime
        );

        source.connect(highPassFilter);
        const destination =
          audioContextRef.current.createMediaStreamDestination();
        highPassFilter.connect(destination);

        const mediaRecorder = new MediaRecorder(destination.stream);
        setMediaRecorder(mediaRecorder);
        mediaRecorder.start();

        mediaRecorder.onerror = (event) => {
          console.error("MediaRecorder error:", (event as ErrorEvent).error);
        };
      } catch (error) {
        console.error("Failed to access microphone:", error);
        handleEndCall();
      }
    };

    startRecording();

    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      if (mediaRecorder) {
        mediaRecorder.stop();
      }
      stopTimer();
    };
  }, []);

  useEffect(() => {
    initializeCall();
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const toggleMute = () => {
    if (!stream) return;
    const audioTrack = stream.getAudioTracks()[0];
    if (!audioTrack) return;

    audioTrack.enabled = !audioTrack.enabled;
    setIsMuted(!audioTrack.enabled);

    if (retellWebClient.mute) {
      if (!audioTrack.enabled) {
        retellWebClient.mute();
      } else {
        retellWebClient.unmute();
      }
    }
  };

  return (
    <>
      {/* Desktop Version - Hidden on mobile */}
      <div className="hidden lg:flex min-h-screen bg-black text-white font-manrope">
        {/* Left Sidebar */}
        <div className="flex-1 flex flex-col items-center pl-20">
          <div className="flex w-full justify-start mt-10 ">
            <button
              className="w-14 h-14 bg-gray-700 rounded-full flex items-center justify-center hover:bg-gray-600 transition-colors"
              onClick={() => {
                handleEndCall();
                if (isCalling || callEnded) toggleDialog();
              }}
            >
              <X size={30} className="text-gray-300" />
            </button>
          </div>

          <div className="flex flex-col mt-32 space-y-12">
            {try_speaking && try_speaking.length > 0 && (
              <SidebarSection title="Try Speaking">
                <div className="space-y-3">
                  {try_speaking.map((suggestion, index) => (
                    <SuggestionItem key={index} text={suggestion} />
                  ))}
                </div>
              </SidebarSection>
            )}

            {callHints && callHints.length > 0 && <SidebarSection title="Call Hints">
              <div className="space-y-3">
                {callHints.map((hint: any, index: number) => (
                  <HintItem key={index} text={hint} />
                ))}
              </div>
            </SidebarSection>
            }

            {features && features.length > 0 && <SidebarSection title="Features">
              <div className="space-y-3">
                {features.map((feature, index) => (
                  <FeatureItem key={index} text={feature} />
                ))}
              </div>
            </SidebarSection>
            }
          </div>
        </div>

        {/*Center widget */}
        <div className="flex-1 flex flex-col items-center justify-start mt-12 space-y-16 pr-8">
          <div className="text-center">
            <h1 className="text-3xl font-normal text-gray-300 font-inter">
              {callTitle}
            </h1>
          </div>

          <VoiceCallInterface
            isMuted={isMuted}
            isCalling={isCalling}
            setIsMuted={setIsMuted}
            toggleMute={toggleMute}
            agentName={agentName}
            onEndCall={handleEndCall}
            onChat={handleChat}
            mediaRecorder={mediaRecorder}
            icon={icon}
            seconds={seconds}
            callEnded={callEnded}
          />
        </div>

        {/* Right Sidebar */}
        <div className="flex-1 p-6 justify-start items-center mt-40">
          <div className="flex flex-col space-y-8 pr-24">
            <SidebarSection title="Transcript" className="flex-1">
              <div className="flex flex-col bg-black rounded-lg p-4 h-96 overflow-y-auto space-y-2 scroll-smooth">
                {transcripts.length === 0 && !currentMessage.content ? (
                  <div className="flex flex-col items-center justify-center h-full">
                    <div className="w-16 h-16 mb-4 rounded-full bg-gray-800/50 flex items-center justify-center">
                      <svg
                        className="w-8 h-8 text-gray-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                        />
                      </svg>
                    </div>
                    <h4 className="text-xl font-medium text-gray-500 mb-2">
                      Your conversation with {agentName} will appear here
                    </h4>
                  </div>
                ) : (
                  <>
                    {transcripts.map((message, index) => (
                      <TranscriptMessage
                        key={index}
                        text={message.text}
                        type={message.type}
                      />
                    ))}

                    {currentMessage.role && currentMessage.content && (
                      <div className="opacity-75">
                        <TranscriptMessage
                          text={currentMessage.content + "..."}
                        />
                      </div>
                    )}
                  </>
                )}
                <div ref={transcriptDesktopRef} />
              </div>
            </SidebarSection>

            {activities && activities.length > 0 && <SidebarSection title="Activities">
              <div className="space-y-6">
                {activities.map((activity, index) => (
                  <ActivityItem
                    key={index}
                    text={activity}
                    icon={Zap}
                    status="active"
                  />
                ))}
              </div>
            </SidebarSection>
            }
          </div>
        </div>
      </div>

      {/* Mobile Version - Hidden on desktop */}

      <div className="flex lg:hidden min-h-screen bg-black text-white flex-col font-manrope">
        <div className="flex items-center p-4">
          <button
            className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center hover:bg-gray-600 transition-colors"
            onClick={() => {
              handleEndCall();
              if (isCalling || callEnded) toggleDialog();
            }}
          >
            <X size={20} className="text-gray-300" />
          </button>

          <h1 className="text-lg font-normal text-gray-300 font-inter ml-4">
            {callTitle}
          </h1>
        </div>

        {/* Voice Call Interface */}

        <div className="flex justify-center ">
          <VoiceCallInterface
            isMuted={isMuted}
            isCalling={isCalling}
            setIsMuted={setIsMuted}
            toggleMute={toggleMute}
            agentName={agentName}
            onEndCall={handleEndCall}
            onChat={handleChat}
            mediaRecorder={mediaRecorder}
            icon={icon}
            seconds={seconds}
            callEnded={callEnded}
          />
        </div>

        {/* Tab Navigation */}
        <div className="flex-shrink-0 px-12">
          <div className="flex overflow-x-auto scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-shrink-0 px-4 py-3 text-sm font-medium transition-colors relative ${activeTab === tab.id
                  ? "text-white"
                  : "text-gray-400 hover:text-gray-300"
                  }`}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 w-[100vw] p-4  justify-center">
          {renderMobileTabContent()}
        </div>
      </div>
    </>
  );
};

export default RetellCallDashboard;
